import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../config';
import '../styles/admin.css';

function CargaPage() {
  const { logout, token } = useAuth();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState([]);
  const [checkedOrders, setCheckedOrders] = useState(new Set());

  const fetchOrders = async () => {
    if (!token) {
      setError('No token available');
      setIsLoading(false);
      return [];
    }

    try {
      const response = await fetch(`${API_BASE}/get-filtered-pos/?status=paid`, {
        headers: { 'Authorization': `Token ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      if (Array.isArray(data)) {
        // Save to localStorage with timestamp
        const cacheData = {
          timestamp: Date.now(),
          data: data
        };
        localStorage.setItem(`orders_paid`, JSON.stringify(cacheData));
        return data;
      }
      return [];
    } catch (error) {
      console.error('Error fetching orders:', error);
      setError('Error al cargar los pedidos. Por favor, intente de nuevo.');
      return [];
    }
  };

  // Load cached data if available and not too old (less than 5 minutes)
  const loadCachedData = () => {
    const cached = localStorage.getItem(`orders_paid`);
    if (cached) {
      try {
        const { timestamp, data } = JSON.parse(cached);
        const isStale = Date.now() - timestamp > 5 * 60 * 1000; // 5 minutes
        if (!isStale) {
          // Filter out checked orders from cache
          const filteredData = data.filter(order => !checkedOrders.has(order.po_number));
          console.log('Loading from cache:', filteredData.length, 'orders');
          return filteredData;
        }
      } catch (e) {
        console.error('Error parsing cached data:', e);
      }
    }
    return null;
  };

  // Initial load with cache
  useEffect(() => {
    const loadOrders = async () => {
      setIsLoading(true);
      setError(null);

      // Try to load from localStorage first
      const cachedData = loadCachedData();
      if (cachedData) {
        setOrders(cachedData);
        setIsLoading(false);
      }

      // Then fetch fresh data in background
      try {
        const data = await fetchOrders();
        // Filter out checked orders from fresh data
        const filteredData = data.filter(order => !checkedOrders.has(order.po_number));
        setOrders(filteredData);
      } catch (error) {
        console.error('Error fetching orders:', error);
        setError('Error al cargar los pedidos. Por favor, intente de nuevo.');
      } finally {
        setIsLoading(false);
      }
    };

    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Background refresh every 30 seconds
  useEffect(() => {
    const backgroundRefresh = async () => {
      if (isBackgroundLoading) return;

      setIsBackgroundLoading(true);
      try {
        const data = await fetchOrders();
        setOrders(prevOrders => {
          // Keep local state for checked orders
          const newOrders = data.filter(order => !checkedOrders.has(order.po_number));
          return newOrders;
        });
      } finally {
        setIsBackgroundLoading(false);
      }
    };

    const intervalId = setInterval(backgroundRefresh, 30000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isBackgroundLoading, checkedOrders]);

  const handleLogout = () => {
    logout();
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const data = await fetchOrders();
      // Filter out checked orders
      const filteredData = data.filter(order => !checkedOrders.has(order.po_number));
      setOrders(filteredData);
    } catch (error) {
      console.error('Error refreshing orders:', error);
      setError('Error al actualizar los pedidos. Por favor, intente de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrderClick = async (order) => {
    try {
      // First try to use the existing order data
      if (order && order.items) {
        navigate(`/paid/${order.po_number}`, {
          state: {
            orderData: order,
            onOrderChecked: (poNumber) => {
              console.log('Order checked:', poNumber);
              // Update checked orders set
              setCheckedOrders(prev => new Set([...prev, poNumber]));

              // Update orders state
              setOrders(prevOrders => {
                const newOrders = prevOrders.filter(o => o.po_number !== poNumber);
                console.log('Updated orders list:', newOrders.length, 'orders');
                return newOrders;
              });

              // Update localStorage
              const cached = localStorage.getItem(`orders_paid`);
              if (cached) {
                try {
                  const { timestamp, data } = JSON.parse(cached);
                  const updatedCachedOrders = data.filter(o => o.po_number !== poNumber);
                  localStorage.setItem(`orders_paid`, JSON.stringify({
                    timestamp,
                    data: updatedCachedOrders
                  }));
                  console.log('Updated cache:', updatedCachedOrders.length, 'orders');
                } catch (e) {
                  console.error('Error updating cached data:', e);
                }
              }
            }
          }
        });
        return;
      }

      // If the order doesn't have items, fetch from get-po endpoint
      const response = await fetch(`${API_BASE}/get-po/?po_number=${order.po_number}`, {
        headers: { 'Authorization': `Token ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch order details');
      }

      const orderData = await response.json();
      navigate(`/paid/${order.po_number}`, {
        state: {
          orderData,
          onOrderChecked: (poNumber) => {
            // Update checked orders set
            setCheckedOrders(prev => new Set([...prev, poNumber]));

            // Update orders state
            setOrders(prevOrders => prevOrders.filter(o => o.po_number !== poNumber));

            // Update localStorage
            const cached = localStorage.getItem(`orders_paid`);
            if (cached) {
              try {
                const { timestamp, data } = JSON.parse(cached);
                const updatedCachedOrders = data.filter(o => o.po_number !== poNumber);
                localStorage.setItem(`orders_paid`, JSON.stringify({
                  timestamp,
                  data: updatedCachedOrders
                }));
              } catch (e) {
                console.error('Error updating cached data:', e);
              }
            }
          }
        }
      });
    } catch (error) {
      console.error('Error fetching order details:', error);
      // Navigate anyway, let the destination component handle the data fetching if needed
      navigate(`/paid/${order.po_number}`);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (!order || !order.po_number) return false;
    return order.po_number.toString().includes(searchValue.trim());
  });

  return (
    <div className="admin-home">
      <button className="adminproductos-back-button" onClick={() => navigate('/init')}>
        &#8592;
      </button>

      <button className="refresh-button" onClick={handleRefresh}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 489.645 489.645" width="24" height="24" fill="currentColor">
          <path d="M460.656,132.911c-58.7-122.1-212.2-166.5-331.8-104.1c-9.4,5.2-13.5,16.6-8.3,27c5.2,9.4,16.6,13.5,27,8.3 c99.9-52,227.4-14.9,276.7,86.3c65.4,134.3-19,236.7-87.4,274.6c-93.1,51.7-211.2,17.4-267.6-70.7l69.3,14.5 c10.4,2.1,21.8-4.2,23.9-15.6c2.1-10.4-4.2-21.8-15.6-23.9l-122.8-25c-20.6-2-25,16.6-23.9,22.9l15.6,123.8 c1,10.4,9.4,17.7,19.8,17.7c12.8,0,20.8-12.5,19.8-23.9l-6-50.5c57.4,70.8,170.3,131.2,307.4,68.2 C414.856,432.511,548.256,314.811,460.656,132.911z" />
        </svg>
      </button>

      <button className="logout-button" onClick={handleLogout}>
        Cerrar Sesión
      </button>

      <h1 className="po-heading">Carga</h1>

      <div className="button-group">
        <button className="pickerhome-cartcode-button" onClick={() => navigate('/cartcode')}>
          Identificador
        </button>
        <button className="pickerhome-cartcode-button liberar-button" onClick={() => navigate('/cart-free')}>
          Liberar
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Buscar por PO Number..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>

      <div className="po-list">
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : filteredOrders.length > 0 ? (
          filteredOrders.map((order, index) => (
            <div key={`${order.po_number}-${index}`} className={`po-item ${order.status}`}>
              <div className="po-number">{order.po_number}.</div>
              <div className="po-info">
                <span className="po-link" onClick={() => handleOrderClick(order)}>
                  <span className="po-client">{order.client}</span>
                  <br />
                  <span className="po-address">{order.address}</span>
                </span>
              </div>
              <div className={`status-circle ${order.status}`}></div>
            </div>
          ))
        ) : (
          <p className="no-orders">No hay pedidos disponibles.</p>
        )}
      </div>
    </div>
  );
}

export default CargaPage;
